/**
 * MyFollowUpTasksWidget — "My Open Tasks" on Home, with a supervisor's team view.
 *
 * Bill 2026-10-16: "How is the user to understand what the listed tasks are in reference to? Shouldn't they be
 * grouped by account, cart or customer ... Modern, sleek, intuitive, cohesive, familiar, guiding and instructive",
 * "Due date, etc.", "it would be a shame for the app and card not be cohesive", and "Supervisors Task View and the
 * ability to reassign tasks".
 *
 * Mine: one collapsible group per customer application (GET v3/follow-up-tasks/my-applications), built from the SAME
 * model as the officer's "Cart processed" Teams card: customer, each opened account with its own tasks, then
 * application-wide tasks, progress and due dates (Task templates in the admin portal; "No due date set" otherwise).
 *
 * My team / All (Supervisor / Manager / Admin; the shared Mine | My team | All switch in teamScope.tsx): the team's or
 * everyone's open tasks (GET v3/follow-up-tasks/team-applications?view=; the server decides who is in each view from the
 * sign-in), officer chips with open and overdue counts, who holds each group, and Reassign… (ReassignTasksDialog →
 * POST v3/follow-up-tasks/reassign, reason required, recorded).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import {
  AlertTriangle, ArrowRight, BookCheck, ChevronDown, CircleCheck, ClipboardCheck, ClipboardList, CloudUpload,
  CreditCard, FileText, Files, Globe, IdCard, MailCheck, MapPin, Package, PenLine, UserRoundCog,
} from 'lucide-react';
import type {
  ApplicationTask, ApplicationTaskGroup, MyApplicationTasksResponse, TeamApplicationTasksResponse, TeamTaskMember,
} from '@retail-ops/shared-types';
import { OfficerBadge } from '@/components/common/OfficerBadge';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useStaffProfiles, profileFor } from '@/hooks/useStaffProfiles';
import { useOfficerEmailLookup, useOfficerNameLookup } from '@/hooks/useOfficerCodeLookup';
import { operatorCode } from '@/utils/operatorCode';
import { ReassignTasksDialog } from './ReassignTasksDialog';
import { ScopeSwitch, useScopeMode, useScopeViews, type ScopeTitles } from './teamScope';
import { accountsLine, acctNumber, allTasks, dueInfo, groupDue, holderOf, type DueInfo } from './taskGroups';

const EXPANDED_KEY = 'rop.home.tasks.expanded';
const TITLES: ScopeTitles = { mine: 'My Open Tasks', team: 'Team Open Tasks', all: 'All Open Tasks' };

interface State<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

const ICONS: Record<string, ReactNode> = {
  'debit-card-print': <CreditCard size={15} aria-hidden />,
  'check-order': <Package size={15} aria-hidden />,
  'aurora-upload': <CloudUpload size={15} aria-hidden />,
  'banno-enable': <Globe size={15} aria-hidden />,
  'estatement-verify': <MailCheck size={15} aria-hidden />,
  'disclosure-copies': <Files size={15} aria-hidden />,
  'signature-outstanding': <PenLine size={15} aria-hidden />,
  'remote-signature': <PenLine size={15} aria-hidden />,
  'cip-core-entry': <IdCard size={15} aria-hidden />,
  'w9-followup': <FileText size={15} aria-hidden />,
  'address-verification': <MapPin size={15} aria-hidden />,
};

function groupKey(g: ApplicationTaskGroup): string {
  return g.cartId ?? `cif:${g.cif ?? g.cifLast4 ?? g.customer}`;
}

function readSession<T>(key: string, parse: (raw: string) => T): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* per-viewer convenience only */ }
}

/** Shared team context for groups in the team view. */
interface TeamCtx {
  members: ReadonlyArray<TeamTaskMember>;
  profiles: ReturnType<typeof useStaffProfiles>;
  /** Absent for view-only roles (Executive): no Reassign button. */
  onReassign?: (g: ApplicationTaskGroup) => void;
}

function memberOf(team: TeamCtx, code?: string | null): TeamTaskMember | undefined {
  return team.members.find((m) => m.officerCode.toUpperCase() === (code ?? '').toUpperCase());
}

function Badge({ team, code, size = 22 }: { team: TeamCtx; code: string; size?: number }): JSX.Element {
  const m = memberOf(team, code);
  return <OfficerBadge code={code} upn={m?.email} profile={profileFor(team.profiles, m?.email)} fallbackName={m?.name ?? code} size={size} />;
}

function DueChip({ info, compact = false }: { info: DueInfo; compact?: boolean }): JSX.Element {
  const title = info.tone === 'none'
    ? 'No due time is configured for this kind of task. An administrator sets it in the admin portal: Retail > Workflow > Task templates.'
    : undefined;
  return (
    <span className={`tg-due tg-due--${info.tone}${compact ? ' tg-due--compact' : ''}`} title={title}>
      {info.tone === 'overdue' && <AlertTriangle size={12} aria-hidden />}
      {info.tone === 'done' && <CircleCheck size={12} aria-hidden />}
      {info.label}
    </span>
  );
}

function Progress({ done, total }: { done: number; total: number }): JSX.Element {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <span className="tg-progress" aria-label={`${done} of ${total} tasks done`}>
      <span className="tg-progress__bar" aria-hidden><span className="tg-progress__fill" style={{ width: `${pct}%` }} /></span>
      <span className="tg-progress__text">{done} of {total} done</span>
    </span>
  );
}

function TaskRow({ t, today, onOpen, team }: { t: ApplicationTask; today: string; onOpen: () => void; team?: TeamCtx }): JSX.Element {
  const info = dueInfo(t.dueDate, today, t.done);
  const holder = team && t.assignedOfficerCode ? memberOf(team, t.assignedOfficerCode)?.name ?? t.assignedOfficerCode : null;
  return (
    <li className={`tg-task${t.done ? ' tg-task--done' : ''}`}>
      <button type="button" className="tg-task__row" onClick={onOpen}
        aria-label={`${t.label}${t.who ? ` for ${t.who}` : ''}${holder ? `, with ${holder}` : ''}. ${info.label}. Opens the application.`}>
        <span className={`tg-task__icon tg-task__icon--${info.tone}`}>
          {t.done ? <CircleCheck size={15} aria-hidden /> : (ICONS[t.category] ?? <ClipboardCheck size={15} aria-hidden />)}
        </span>
        <span className="tg-task__text">
          <span className="tg-task__label">{t.label}</span>
          {(t.who || holder) && (
            <span className="tg-task__who">{[t.who ? `for ${t.who}` : '', holder && !t.done ? `with ${holder}` : ''].filter(Boolean).join(' · ')}</span>
          )}
        </span>
        {t.priority === 'High' && !t.done && <span className="tg-pri">High priority</span>}
        <DueChip info={info} compact />
      </button>
    </li>
  );
}

interface Holder { code: string; upn?: string | null; name: string; profile: ReturnType<typeof profileFor> }

function Group({ g, today, open, onToggle, team, holder }: {
  g: ApplicationTaskGroup; today: string; open: boolean; onToggle: () => void; team?: TeamCtx; holder: Holder | null;
}): JSX.Element {
  const navigate = useNavigate();
  const tasks = allTasks(g);
  const due = groupDue(g, today);
  const target = g.kind === 'application' && g.cartId
    ? `/rao/${encodeURIComponent(g.cartId)}`
    : g.cif ? `/rcm/${encodeURIComponent(g.cif)}` : null;
  const openTarget = (): void => { if (target) navigate(target); };
  const bodyId = `tg-body-${groupKey(g).replace(/[^a-zA-Z0-9-]/g, '')}`;
  const subline = g.kind === 'application' ? accountsLine(g) || 'Account opening' : `Customer ··${g.cifLast4 ?? ''}`;
  const others = Math.max(0, (g.assignees ?? []).length - 1);

  return (
    <li className={`tg${open ? ' tg--open' : ''}${g.overdue ? ' tg--overdue' : ''}`}>
      <div className="tg__row">
        {/* The officer, as on the cart panels; beside the button, never inside it (it opens the profile card). */}
        <span className="tg__officer">
          {holder && (
            <OfficerBadge code={holder.code} upn={holder.upn} profile={holder.profile} fallbackName={holder.name}
              {...(g.cartId ? { reference: { title: g.customer, cartId: g.cartId } } : {})} />
          )}
        </span>
      <button type="button" className="tg__head" aria-expanded={open} aria-controls={bodyId} onClick={onToggle}
        aria-label={`${g.customer}${holder ? `, with ${holder.name}${others ? ` and ${others} more` : ''}` : ''}. ${g.openCount} open. ${due.label}.`}>
        <span className="tg__main">
          <span className="tg__name">{g.customer}</span>
          <span className="tg__sub">{subline}</span>
          <Progress done={g.doneCount} total={tasks.length} />
        </span>
        <span className="tg__side">
          <DueChip info={due} />
          <span className="tg__left">
            {g.openCount} open{others > 0 ? ` · +${others} more` : ''}
          </span>
        </span>
        <ChevronDown size={16} aria-hidden className="tg__chev" />
      </button>
      </div>

      {open && (
        <div className="tg__body" id={bodyId}>
          {g.accounts.filter((a) => a.tasks.length > 0).map((a, i) => (
            <section key={`${a.acctId ?? a.product}-${i}`} className="tg-acct">
              <header className="tg-acct__head">
                <span className="tg-acct__product">{a.product}</span>
                {acctNumber(a.acctId) && <span className="tg-acct__num">{acctNumber(a.acctId)}</span>}
                {a.owners.length > 0 && <span className="tg-acct__owners">{a.owners.join(', ')}</span>}
              </header>
              <ul className="tg-tasks">
                {a.tasks.map((t, j) => <TaskRow key={t.id ?? `${t.category}-${j}`} t={t} today={today} onOpen={openTarget} team={team} />)}
              </ul>
            </section>
          ))}
          {g.applicationTasks.length > 0 && (
            <section className="tg-acct">
              <header className="tg-acct__head">
                <span className="tg-acct__product">{g.kind === 'application' ? 'Whole application' : 'Customer'}</span>
              </header>
              <ul className="tg-tasks">
                {g.applicationTasks.map((t, j) => <TaskRow key={t.id ?? `${t.category}-${j}`} t={t} today={today} onOpen={openTarget} team={team} />)}
              </ul>
            </section>
          )}
          <footer className="tg__foot">
            <span className="tg__hint">
              {team?.onReassign ? 'Move tasks to someone else on your team, or open the application.' : team ? 'Open the application to see where it stands.' : 'Complete each task from the application’s finished screen.'}
            </span>
            <span className="tg__actions">
              {team?.onReassign && g.openCount > 0 && (
                <button type="button" className="tg__reassign" onClick={() => team.onReassign?.(g)}>
                  <UserRoundCog size={14} aria-hidden /> Reassign…
                </button>
              )}
              {target && (
                <button type="button" className="tg__open" onClick={openTarget}>
                  {g.kind === 'application' ? 'Open in Retail' : 'Open customer'} <ArrowRight size={14} aria-hidden />
                </button>
              )}
            </span>
          </footer>
        </div>
      )}
    </li>
  );
}

export function MyFollowUpTasksWidget(): JSX.Element {
  const { user } = useAuth();
  const client = useApiClient();
  const officer = operatorCode(user?.officerCode);
  const views = useScopeViews();
  const [mode, setMode] = useScopeMode('rop.home.tasks.scope', views);
  const [mine, setMine] = useState<State<MyApplicationTasksResponse>>({ data: null, loading: true, error: null });
  const [team, setTeam] = useState<State<TeamApplicationTasksResponse>>({ data: null, loading: false, error: null });
  const [officerFilter, setOfficerFilter] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string> | null>(() => readSession(EXPANDED_KEY, (r) => new Set(JSON.parse(r) as string[])));
  const [reassigning, setReassigning] = useState<ApplicationTaskGroup | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (mode !== 'mine') return undefined;
    if (!officer) { setMine({ data: null, loading: false, error: null }); return undefined; }
    const ctrl = new AbortController();
    setMine((p) => ({ ...p, loading: true, error: null }));
    client.retail.followUpTasks.myApplications(officer, { signal: ctrl.signal })
      .then((data) => { if (!ctrl.signal.aborted) setMine({ data, loading: false, error: null }); })
      .catch((err: unknown) => {
        if (!ctrl.signal.aborted) setMine({ data: null, loading: false, error: err instanceof Error ? err.message : 'Unable to load tasks.' });
      });
    return () => ctrl.abort();
  }, [client, officer, mode, reloadKey]);

  useEffect(() => {
    if (mode === 'mine') return undefined;
    const ctrl = new AbortController();
    setTeam((p) => ({ ...p, loading: true, error: null }));
    client.retail.followUpTasks.teamApplications(mode, undefined, { signal: ctrl.signal })
      .then((data) => { if (!ctrl.signal.aborted) setTeam({ data, loading: false, error: null }); })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        const status = (err as { httpStatus?: number } | null)?.httpStatus;
        if (status === 403) {
          // The server no longer offers this view (for example the admin narrowed it): back to Mine.
          setMode('mine');
          setTeam({ data: null, loading: false, error: null });
          return;
        }
        setTeam({ data: null, loading: false, error: err instanceof Error ? err.message : 'Unable to load your team’s tasks.' });
      });
    return () => ctrl.abort();
  }, [client, mode, reloadKey]);

  const members = team.data?.team ?? [];
  const emailFor = useOfficerEmailLookup();
  const officerName = useOfficerNameLookup();
  const active = mode !== 'mine' ? team : mine;
  const today = active.data?.today ?? new Date().toISOString().slice(0, 10);
  const allGroups = active.data?.groups ?? [];
  const groups = useMemo(
    () => (mode !== 'mine' && officerFilter
      ? allGroups.filter((g) => (g.assignees ?? []).some((a) => a.toUpperCase() === officerFilter.toUpperCase()))
      : allGroups),
    [allGroups, mode, officerFilter],
  );
  // The officer shown on each group (and the team chips): members' e-mails in team views, the officer lookup otherwise.
  const holderCodes = useMemo(() => groups.map(holderOf), [groups]);
  const upnFor = (code: string | null): string | null =>
    !code ? null : members.find((m) => m.officerCode.toUpperCase() === code)?.email ?? emailFor(code) ?? null;
  const profiles = useStaffProfiles([...members.map((m) => m.email ?? null), ...holderCodes.map(upnFor)]);
  const holderFor = (i: number): Holder | null => {
    const code = holderCodes[i];
    if (!code) return null;
    const upn = upnFor(code);
    const m = members.find((x) => x.officerCode.toUpperCase() === code);
    return { code, upn, profile: profileFor(profiles, upn), name: profileFor(profiles, upn)?.name || m?.name || officerName(code, null) || code };
  };
  // CHG-0147 (Reid's review 10/30): every group starts collapsed; the FSR opens the one they want.
  const openKeys = useMemo(() => expanded ?? new Set<string>(), [expanded]);
  const toggle = (key: string): void => {
    const next = new Set(openKeys);
    if (next.has(key)) next.delete(key); else next.add(key);
    setExpanded(next);
    writeSession(EXPANDED_KEY, JSON.stringify([...next]));
  };
  const switchMode = (m: typeof mode): void => {
    setMode(m);
    setOfficerFilter(null);
    setNotice(null);
  };
  const teamCtx: TeamCtx | undefined = mode !== 'mine'
    ? { members, profiles, ...(views.canReassign ? { onReassign: (g: ApplicationTaskGroup) => { setNotice(null); setReassigning(g); } } : {}) }
    : undefined;
  const onReassigned = useCallback((moved: number, toName: string) => {
    setReassigning(null);
    setNotice(`${moved} task${moved === 1 ? '' : 's'} moved to ${toName}.`);
    setReloadKey((k) => k + 1);
  }, []);
  const overdueCount = groups.filter((g) => g.overdue).length;
  const openCount = mode !== 'mine' && officerFilter
    ? members.find((m) => m.officerCode === officerFilter)?.openCount ?? 0
    : active.data?.openCount ?? 0;

  return (
    <section className="home-card home-card--tasks" aria-label={TITLES[mode]}>
      <header className="home-card__head">
        <div className="home-card__head-left">
          <ClipboardList size={16} aria-hidden className="home-card__head-icon" />
          <h2 className="home-card__title">{TITLES[mode]}</h2>
          <span className="home-card__count">{active.loading ? '…' : openCount}</span>
          {overdueCount > 0 && <span className="tg-head-overdue">{overdueCount} overdue</span>}
        </div>
      </header>
      <ScopeSwitch mode={mode} views={views} onChange={switchMode} label="tasks" />

      <div className="home-card__body">
        {mode !== 'mine' && members.length > 0 && (
          <div className="tg-people" role="toolbar" aria-label="Filter by officer">
            <button type="button" className={`tg-person${officerFilter === null ? ' is-on' : ''}`} onClick={() => setOfficerFilter(null)}>
              Everyone <span className="tg-person__n">{team.data?.openCount ?? 0}</span>
            </button>
            {members.filter((m) => m.openCount > 0).map((m) => (
              <button key={m.officerCode} type="button" className={`tg-person${officerFilter === m.officerCode ? ' is-on' : ''}`}
                onClick={() => setOfficerFilter(officerFilter === m.officerCode ? null : m.officerCode)}
                aria-label={`${m.name}: ${m.openCount} open${m.overdueCount ? `, ${m.overdueCount} overdue` : ''}`}>
                {teamCtx && <Badge team={teamCtx} code={m.officerCode} size={18} />}
                <span className="tg-person__name">{m.name.split(' ')[0]}</span>
                <span className="tg-person__n">{m.openCount}</span>
                {m.overdueCount > 0 && <span className="tg-person__late">{m.overdueCount} late</span>}
              </button>
            ))}
          </div>
        )}

        {notice && <div className="tg-notice" role="status"><CircleCheck size={14} aria-hidden /> {notice}</div>}

        {active.loading ? (
          <ul className="home-skel">
            {[0, 1, 2].map((i) => (
              <li key={i} className="home-skel__row">
                <span className="home-skel__bar home-skel__bar--lg" />
                <span className="home-skel__bar home-skel__bar--sm" />
              </li>
            ))}
          </ul>
        ) : active.error ? (
          <div className="home-card__error"><AlertTriangle size={14} aria-hidden /> {active.error}</div>
        ) : mode === 'mine' && !officer ? (
          <div className="home-card__empty">
            <p>Your officer code isn&rsquo;t set up yet, so your tasks can&rsquo;t be shown. Ask an administrator to check your user record.</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="home-card__empty">
            <BookCheck size={22} aria-hidden className="tg-empty-icon" />
            <p><strong>{mode === 'mine' ? 'You’re all caught up.' : mode === 'team' ? 'Your team is all caught up.' : 'Everyone is caught up.'}</strong></p>
            <p>After an account opens, its follow-ups (printing the card, uploading to Aurora, turning on online banking) show up here, grouped by customer.</p>
          </div>
        ) : (
          <>
            <p className="tg-intro">
              {mode === 'mine'
                ? 'Grouped by customer, most urgent first. Open a customer to see each account’s tasks.'
                : `${mode === 'team' ? 'Your team’s' : 'Everyone’s'} open tasks by customer, most urgent first. Open a customer to see who has each task or move it.`}
            </p>
            <ul className="tg-list">
              {groups.map((g, i) => (
                <Group key={groupKey(g)} g={g} today={today} open={openKeys.has(groupKey(g))} onToggle={() => toggle(groupKey(g))} team={teamCtx} holder={holderFor(i)} />
              ))}
            </ul>
          </>
        )}
      </div>

      <ReassignTasksDialog group={reassigning} team={members} onClose={() => setReassigning(null)} onDone={onReassigned} />
    </section>
  );
}
